/**
 * apps/web :: components/whatsnext :: AaaMembershipSection
 *
 * Section 6 of the post-pass journey (ROADS-431).
 *
 * Static editorial about the American Automobile Association (AAA): a
 * national membership organisation that provides roadside assistance,
 * trip planning, and DMV-document services in many states. We frame
 * this neutrally — AAA is one of several roadside-assistance options
 * (insurance carriers and credit-card concierge programs are common
 * alternatives). No affiliate code yet (ROADS-432).
 *
 * The link points at AAA's national `https://www.aaa.com/` so the
 * learner is routed to their regional club automatically.
 */
import { useTranslation } from "react-i18next";

const AAA_NATIONAL_URL = "https://www.aaa.com/";

const BENEFIT_KEYS = [
  "roadside",
  "tripPlanning",
  "dmvServices",
  "discounts",
] as const;

export default function AaaMembershipSection() {
  const { t } = useTranslation();
  return (
    <section
      data-testid="whatsnext-section-aaa"
      aria-labelledby="whatsnext-aaa-heading"
      className="rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
    >
      <h2
        id="whatsnext-aaa-heading"
        className="font-display text-xl font-bold text-brand-primary"
      >
        {t("whatsnext.aaa.heading")}
      </h2>
      <p className="mt-sm text-neutral-700">{t("whatsnext.aaa.intro")}</p>

      <ul
        className="mt-md grid gap-sm sm:grid-cols-2"
        data-testid="whatsnext-aaa-benefits"
      >
        {BENEFIT_KEYS.map((key) => (
          <li
            key={key}
            className="rounded-md border border-neutral-200 bg-slate-50 p-sm text-sm text-neutral-800"
            data-testid={`whatsnext-aaa-benefit-${key}`}
          >
            <span className="font-semibold text-brand-primary">
              {t(`whatsnext.aaa.benefits.${key}.title`)}
            </span>
            {": "}
            {t(`whatsnext.aaa.benefits.${key}.body`)}
          </li>
        ))}
      </ul>

      <p className="mt-md text-sm text-neutral-600">
        {t("whatsnext.aaa.alternativesNote")}
      </p>

      <a
        href={AAA_NATIONAL_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-md inline-flex items-center gap-2 rounded-md border border-brand-primary bg-white px-md py-sm text-sm font-semibold text-brand-primary no-underline hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
      >
        {t("whatsnext.aaa.applyLink")}
        <span aria-hidden="true">→</span>
      </a>
    </section>
  );
}
